import { onMounted, onUnmounted, ref } from 'vue';
import type { Ref } from 'vue';

/**
 * Reactive media-query match. Returns false when window.matchMedia is
 * unavailable (jsdom under Vitest) so callers degrade to the "no match"
 * branch instead of throwing.
 */
export function useMediaQuery(query: string): Ref<boolean> {
  let mql: MediaQueryList | null = null;
  let initialState = false;

  // Try to initialize synchronously if window.matchMedia is available
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mql = window.matchMedia(query);
    initialState = mql.matches;
  }

  const matches = ref(initialState);
  const update = () => { matches.value = mql ? mql.matches : false; };

  onMounted(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    mql = window.matchMedia(query);
    update();
    mql.addEventListener('change', update);
  });
  onUnmounted(() => {
    mql?.removeEventListener('change', update);
    mql = null;
  });

  return matches;
}

/** True at Bootstrap's `lg` breakpoint and up (>= 992px). */
export function useIsDesktop(): Ref<boolean> {
  return useMediaQuery('(min-width: 992px)');
}
